import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPartnerOffers,
  getPartnerOffersForExport,
  getPartnerOfferCategories,
  getStateClassifications,
  getApiKeys,
  getOrganizationsForDropdown,
  getAllTags,
  getOfferStats,
  createPartnerOffer,
  archivePartnerOffer,
  updateStateClassification,
  createApiKey,
  revokeApiKey,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../services/partnerOffers.service";
import type {
  PartnerOffer,
  PartnerOfferCategory,
  PartnerOfferFormData,
  PartnerOfferFilters,
  PartnerOfferSort,
  PartnerOfferSortField,
  StateInsuranceClassification,
  PartnerOfferApiKey,
  PartnerOfferStatus,
  PartnerOfferBrand,
} from "../types/partnerOffer.types";
// Value import — PartnerOfferKind is used as a value (form default + comparisons).
import { PartnerOfferKind } from "../types/partnerOffer.types";
import { exportToCSV } from "../utils/dataImportExport";

type TabType = "offers" | "categories" | "states" | "api-keys";

const STATUS_COLORS: Record<PartnerOfferStatus, string> = {
  draft: "bg-gray-100 text-gray-800",
  scheduled: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  paused: "bg-yellow-100 text-yellow-800",
  expired: "bg-red-100 text-red-800",
  archived: "bg-gray-100 text-gray-600",
};

const BRAND_LABELS: Record<PartnerOfferBrand, string> = {
  usls_only: "USLS",
  emergency_shield_only: "EmergencyShield",
  both: "Both",
};

interface Organization {
  id: string;
  name: string;
}

interface OfferStats {
  offers: {
    total: number;
    active: number;
    draft: number;
    scheduled: number;
    featured: number;
  };
  lifetime: { impressions: number; claims: number; conversionRate: string };
  last7Days: {
    impressions: number;
    clicks: number;
    claims: number;
    redemptions: number;
  };
}

const PartnerOffers: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("offers");
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [categories, setCategories] = useState<PartnerOfferCategory[]>([]);
  const [flatCategories, setFlatCategories] = useState<PartnerOfferCategory[]>(
    [],
  );
  const [states, setStates] = useState<StateInsuranceClassification[]>([]);
  const [apiKeys, setApiKeys] = useState<PartnerOfferApiKey[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [stats, setStats] = useState<OfferStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState<PartnerOfferFilters>({});
  const [sort, setSort] = useState<PartnerOfferSort>({
    field: "created_at",
    ascending: false,
  });
  const [newApiKey, setNewApiKey] = useState<string | null>(null);

  // Category editing
  const [editingCategory, setEditingCategory] =
    useState<PartnerOfferCategory | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    slug: "",
    description: "",
    parent_id: "",
  });

  // Create form state
  const [createForm, setCreateForm] = useState<PartnerOfferFormData>({
    company_name: "",
    headline: "",
    offer_amount: "",
    // FR-04: default Standing (evergreen). Date seeds below are only used if the
    // operator switches to Lightning (they're stripped to NULL on save for Standing).
    offer_kind: PartnerOfferKind.STANDING,
    start_date: new Date().toISOString().split("T")[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    brand_target: "both",
    offer_type: "non_insurance",
    target_all_states: true,
    is_featured: false,
    priority_score: 50,
    status: "draft",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    loadData();
  }, [activeTab, filters, sort]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === "offers") {
        const [offersData, categoriesData, orgsData, tagsData, statsData] =
          await Promise.all([
            getPartnerOffers(filters, sort),
            getPartnerOfferCategories(),
            getOrganizationsForDropdown(),
            getAllTags(),
            getOfferStats(),
          ]);
        setOffers(offersData);
        setCategories(categoriesData);
        setOrganizations(orgsData);
        setExistingTags(tagsData);
        setStats(statsData);
        // Flatten for dropdown
        const flat: PartnerOfferCategory[] = [];
        categoriesData.forEach((cat) => {
          flat.push(cat);
          cat.children?.forEach((child) => flat.push(child));
        });
        setFlatCategories(flat);
      } else if (activeTab === "categories") {
        const data = await getPartnerOfferCategories();
        setCategories(data);
      } else if (activeTab === "states") {
        const data = await getStateClassifications();
        setStates(data);
      } else if (activeTab === "api-keys") {
        const data = await getApiKeys();
        setApiKeys(data);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: PartnerOfferSortField) => {
    if (sort.field === field) {
      setSort({ field, ascending: !sort.ascending });
    } else {
      setSort({ field, ascending: false });
    }
  };

  const handleRowClick = (offerId: string) => {
    navigate(`/partner-offers/${offerId}`);
  };

  // FR-01: export the currently-filtered offer list to CSV. Fetches ALL matching
  // rows (paginated past PostgREST's 1000-row cap — the on-screen list is capped,
  // the export must not be), flattened to readable columns. Company falls back to
  // the source org (company_name is now optional).
  const handleExportOffers = async () => {
    let exportRows: PartnerOffer[];
    try {
      exportRows = await getPartnerOffersForExport(filters, sort);
    } catch (err) {
      console.error("Error fetching offers for export:", err);
      setError("Failed to export offers.");
      return;
    }
    if (exportRows.length === 0) {
      setError("No offers to export.");
      return;
    }
    const rows = exportRows.map((o) => ({
      Company: o.company_name || o.source_organization?.name || "",
      "Source Organization": o.source_organization?.name || "US Law Shield",
      Headline: o.headline,
      "Offer Amount": o.offer_amount,
      "Offer Type":
        o.offer_kind === PartnerOfferKind.LIGHTNING ? "Lightning" : "Standing",
      Category: o.category?.name || "",
      "Start Date": o.start_date || "",
      "End Date": o.end_date || "",
      Brand: o.brand_target,
      Status: o.status,
      Featured: o.is_featured ? "Yes" : "No",
      Impressions: o.total_impressions,
      Claims: o.total_claims,
    }));
    exportToCSV(rows, {
      format: "csv",
      filename: `partner-offers-${new Date().toISOString().split("T")[0]}`,
      columns: [
        "Company",
        "Source Organization",
        "Headline",
        "Offer Amount",
        "Offer Type",
        "Category",
        "Start Date",
        "End Date",
        "Brand",
        "Status",
        "Featured",
        "Impressions",
        "Claims",
      ],
    });
  };

  const handleCreateOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    // FR-03: Source Organization is now the offer's identity (company_name box
    // removed), so it's required in place of company_name. Headline + amount stay.
    if (
      !createForm.source_organization_id ||
      !createForm.headline ||
      !createForm.offer_amount
    ) {
      setError(
        "Please fill in all required fields (Source Organization, Headline, Offer Amount)",
      );
      return;
    }
    // FR-05: Lightning offers are time-boxed — dates are required. Standing
    // offers are evergreen — dates are stripped to NULL below.
    if (
      createForm.offer_kind === PartnerOfferKind.LIGHTNING &&
      (!createForm.start_date || !createForm.end_date)
    ) {
      setError("Lightning offers require a Start and End date.");
      return;
    }
    if (
      createForm.offer_kind === PartnerOfferKind.LIGHTNING &&
      createForm.start_date &&
      createForm.end_date &&
      createForm.end_date < createForm.start_date
    ) {
      setError("End Date must be on or after the Start Date.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      // Standing → evergreen: never persist the seeded dates.
      const payload: PartnerOfferFormData =
        createForm.offer_kind === PartnerOfferKind.STANDING
          ? { ...createForm, start_date: null, end_date: null }
          : createForm;
      const newOffer = await createPartnerOffer(payload);
      setSuccess("Offer created successfully");
      setShowCreateForm(false);
      setCreateForm({
        company_name: "",
        headline: "",
        offer_amount: "",
        offer_kind: PartnerOfferKind.STANDING,
        start_date: new Date().toISOString().split("T")[0],
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        brand_target: "both",
        offer_type: "non_insurance",
        target_all_states: true,
        is_featured: false,
        priority_score: 50,
        status: "draft",
        tags: [],
      });
      navigate(`/partner-offers/${newOffer.id}`);
    } catch (err) {
      console.error("Error creating offer:", err);
      setError("Failed to create offer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to archive this offer?")) {
      return;
    }

    try {
      await archivePartnerOffer(id);
      setSuccess("Offer archived");
      loadData();
    } catch (err) {
      setError("Failed to archive offer");
    }
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) {
      return;
    }
    const newTag = tagInput.trim().toLowerCase();
    if (!createForm.tags?.includes(newTag)) {
      setCreateForm({
        ...createForm,
        tags: [...(createForm.tags || []), newTag],
      });
    }
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setCreateForm({
      ...createForm,
      tags: createForm.tags?.filter((t) => t !== tag) || [],
    });
  };

  const handleStateToggle = async (
    stateCode: string,
    current: "insurance" | "non_insurance",
  ) => {
    try {
      await updateStateClassification(
        stateCode,
        current === "insurance" ? "non_insurance" : "insurance",
      );
      setSuccess(`Updated ${stateCode}`);
      loadData();
    } catch (err) {
      setError("Failed to update state");
    }
  };

  const handleCreateApiKey = async () => {
    const name = prompt("Enter a name for this API key:");
    if (!name) {
      return;
    }

    try {
      const { key } = await createApiKey(name);
      setNewApiKey(key);
      setSuccess("API key created successfully");
      loadData();
    } catch (err) {
      setError("Failed to create API key");
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    if (!confirm("Revoke this API key?")) {
      return;
    }
    try {
      await revokeApiKey(id);
      setSuccess("API key revoked");
      loadData();
    } catch (err) {
      setError("Failed to revoke API key");
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name || !categoryForm.slug) {
      setError("Name and slug are required");
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: categoryForm.name,
          slug: categoryForm.slug,
          description: categoryForm.description || undefined,
          parent_id: categoryForm.parent_id || undefined,
        });
        setSuccess("Category updated");
      } else {
        await createCategory(
          categoryForm.name,
          categoryForm.slug,
          categoryForm.parent_id || undefined,
          categoryForm.description || undefined,
        );
        setSuccess("Category created");
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", slug: "", description: "", parent_id: "" });
      loadData();
    } catch (err) {
      setError("Failed to save category");
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm("Delete this category?")) {
      return;
    }
    try {
      await deleteCategory(id);
      setSuccess("Category deleted");
      loadData();
    } catch (err) {
      setError("Failed to delete category");
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const SortHeader: React.FC<{
    field: PartnerOfferSortField;
    label: string;
  }> = ({ field, label }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sort.field === field && (
          <svg
            className={`w-4 h-4 ${sort.ascending ? "" : "rotate-180"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        )}
      </div>
    </th>
  );

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Partner Offers</h1>
        <p className="text-gray-600 mt-1">
          Manage partner offers, categories, and API access
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "offers" as TabType, label: "Offers" },
            { id: "categories" as TabType, label: "Categories" },
            { id: "states" as TabType, label: "State Classifications" },
            { id: "api-keys" as TabType, label: "API Keys" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-900 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-900 hover:text-green-700"
          >
            ×
          </button>
        </div>
      )}

      {/* New API Key Display */}
      {newApiKey && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="font-medium text-yellow-800 mb-2">
            Copy your API key now - it won't be shown again:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 bg-white border rounded font-mono text-sm break-all">
              {newApiKey}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(newApiKey);
                setSuccess("Copied!");
              }}
              className="px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              Copy
            </button>
            <button
              onClick={() => setNewApiKey(null)}
              className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* OFFERS TAB */}
      {activeTab === "offers" && (
        <div>
          {/* Summary Statistics */}
          {stats && !showCreateForm && (
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Total Offers</div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.offers.total}
                </div>
                <div className="mt-1 flex gap-2 text-xs">
                  <span className="text-green-600">
                    {stats.offers.active} active
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-blue-600">
                    {stats.offers.scheduled} scheduled
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">
                    {stats.offers.draft} draft
                  </span>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">
                  Lifetime Impressions
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.lifetime.impressions.toLocaleString()}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {stats.last7Days.impressions.toLocaleString()} in last 7 days
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Total Claims</div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.lifetime.claims.toLocaleString()}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {stats.last7Days.claims.toLocaleString()} in last 7 days
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Conversion Rate</div>
                <div className="text-2xl font-bold text-primary-600">
                  {stats.lifetime.conversionRate}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {stats.offers.featured} featured offers
                </div>
              </div>
            </div>
          )}

          {showCreateForm ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Offer</h2>
              <form onSubmit={handleCreateOffer} className="space-y-4">
                {/* FR-03: Source Organization is the offer's identity (the old
                    free-text Company Name box was removed). FR-04: Standing vs
                    Lightning classification drives whether dates apply. */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source Organization *
                    </label>
                    <select
                      value={createForm.source_organization_id || ""}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          source_organization_id: e.target.value || undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">Select the partner organization…</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      The organization this offer belongs to. Shown to members
                      as the offer's source.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer Type *
                    </label>
                    <select
                      value={createForm.offer_kind}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          offer_kind: e.target.value as PartnerOfferKind,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={PartnerOfferKind.STANDING}>
                        Standing — always available (no dates)
                      </option>
                      <option value={PartnerOfferKind.LIGHTNING}>
                        Lightning — limited-time (start &amp; end dates)
                      </option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {createForm.offer_kind === PartnerOfferKind.LIGHTNING
                        ? "This offer runs only between the dates you set below."
                        : "This offer stays active indefinitely — no start/end dates."}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Headline *
                    </label>
                    <input
                      type="text"
                      value={createForm.headline}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          headline: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer Amount *
                    </label>
                    <input
                      type="text"
                      value={createForm.offer_amount}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          offer_amount: e.target.value,
                        })
                      }
                      placeholder="e.g., 20% off"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={createForm.category_id || ""}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          category_id: e.target.value || undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select category...</option>
                      {flatCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.parent_id ? `  ${cat.name}` : cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* FR-05: dates apply only to Lightning (time-boxed) offers.
                    Standing offers are evergreen and store NULL dates. */}
                {createForm.offer_kind === PartnerOfferKind.LIGHTNING ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        value={createForm.start_date ?? ""}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            start_date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date *
                      </label>
                      <input
                        type="date"
                        value={createForm.end_date ?? ""}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            end_date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <p className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                    This is a <strong>Standing</strong> offer — it stays active
                    with no start or end date. Switch the Offer Type to{" "}
                    <strong>Lightning</strong> above to set a limited-time
                    window.
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {createForm.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 text-primary-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddTag())
                      }
                      placeholder="Add a tag..."
                      list="tags-list"
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <datalist id="tags-list">
                      {existingTags.map((t) => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {submitting ? "Creating..." : "Create & Edit Details"}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div>
              {/* Filters & Actions */}
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-3">
                  <select
                    value={filters.status || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        status:
                          (e.target.value as PartnerOfferStatus) || undefined,
                      })
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="expired">Expired</option>
                    <option value="archived">Archived</option>
                  </select>
                  <select
                    value={filters.category_id || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        category_id: e.target.value || undefined,
                      })
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="">All Categories</option>
                    {flatCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.parent_id ? `  ${cat.name}` : cat.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filters.source_organization_id || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        source_organization_id: e.target.value || undefined,
                      })
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="">All Sources</option>
                    <option value="internal">US Law Shield (Internal)</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {/* FR-04: filter by Standing / Lightning */}
                  <select
                    value={filters.offer_kind || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        offer_kind:
                          (e.target.value as PartnerOfferKind) || undefined,
                      })
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="">All Offer Types</option>
                    <option value={PartnerOfferKind.STANDING}>Standing</option>
                    <option value={PartnerOfferKind.LIGHTNING}>
                      Lightning
                    </option>
                  </select>
                  <input
                    type="text"
                    placeholder="Search offers or tags..."
                    value={filters.search || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    className="px-3 py-2 border rounded-lg w-64"
                  />
                </div>
                <div className="flex items-center gap-2">
                  {/* FR-01: export the current (filtered) list to CSV */}
                  <button
                    onClick={handleExportOffers}
                    disabled={offers.length === 0}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                    title="Export the current offer list to CSV"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Export
                  </button>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Create Offer
                  </button>
                </div>
              </div>

              {/* Offers Table */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : offers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No offers found</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortHeader
                          field="company_name"
                          label="Company / Offer"
                        />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Source
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Category
                        </th>
                        <SortHeader field="start_date" label="Start" />
                        <SortHeader field="end_date" label="End" />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Brand
                        </th>
                        <SortHeader field="total_impressions" label="Views" />
                        <SortHeader field="total_claims" label="Claims" />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {offers.map((offer) => (
                        <tr
                          key={offer.id}
                          onClick={() => handleRowClick(offer.id)}
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              {offer.company_logo_url && (
                                <img
                                  src={offer.company_logo_url}
                                  alt=""
                                  className="w-8 h-8 rounded object-cover mr-3"
                                />
                              )}
                              <div>
                                <div className="font-medium text-gray-900">
                                  {offer.company_name ||
                                    offer.source_organization?.name ||
                                    "US Law Shield"}
                                </div>
                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                  {offer.headline}
                                </div>
                                {offer.tags?.length > 0 && (
                                  <div className="flex gap-1 mt-1">
                                    {offer.tags.slice(0, 3).map((tag) => (
                                      <span
                                        key={tag}
                                        className="text-xs px-1.5 py-0.5 bg-gray-100 rounded"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                    {offer.tags.length > 3 && (
                                      <span className="text-xs text-gray-400">
                                        +{offer.tags.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.source_organization?.name || (
                              <span className="text-primary-600">USLS</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.category?.name || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.start_date ? (
                              formatDate(offer.start_date)
                            ) : (
                              <span className="text-gray-400">Always</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.end_date ? formatDate(offer.end_date) : "—"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {BRAND_LABELS[offer.brand_target]}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.total_impressions}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {offer.total_claims}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[offer.status]}`}
                            >
                              {offer.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              onClick={(e) => handleArchive(offer.id, e)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Archive
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CATEGORIES TAB */}
      {activeTab === "categories" && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Offer Categories</h2>
            <button
              onClick={() => {
                setShowCategoryForm(true);
                setEditingCategory(null);
                setCategoryForm({
                  name: "",
                  slug: "",
                  description: "",
                  parent_id: "",
                });
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add Category
            </button>
          </div>

          {showCategoryForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-medium mb-3">
                {editingCategory ? "Edit Category" : "New Category"}
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.slug}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        slug: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg font-mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Category
                  </label>
                  <select
                    value={categoryForm.parent_id}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        parent_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">None (Top Level)</option>
                    {categories
                      .filter(
                        (c) => !editingCategory || c.id !== editingCategory.id,
                      )
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveCategory}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingCategory ? "Update" : "Create"}
                </button>
                <button
                  onClick={() => {
                    setShowCategoryForm(false);
                    setEditingCategory(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="space-y-3">
              {categories.map((cat) => (
                <div key={cat.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{cat.name}</span>
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                        {cat.slug}
                      </code>
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${cat.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}
                      >
                        {cat.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setCategoryForm({
                            name: cat.name,
                            slug: cat.slug,
                            description: cat.description || "",
                            parent_id: cat.parent_id || "",
                          });
                          setShowCategoryForm(true);
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {cat.description && (
                    <p className="text-sm text-gray-500 mt-1">
                      {cat.description}
                    </p>
                  )}
                  {cat.children && cat.children.length > 0 && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-2">
                      {cat.children.map((child) => (
                        <div
                          key={child.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{child.name}</span>
                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {child.slug}
                            </code>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingCategory(child);
                                setCategoryForm({
                                  name: child.name,
                                  slug: child.slug,
                                  description: child.description || "",
                                  parent_id: child.parent_id || "",
                                });
                                setShowCategoryForm(true);
                              }}
                              className="text-xs text-primary-600 hover:text-primary-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(child.id)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STATES TAB */}
      {activeTab === "states" && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-2">
            State Insurance Classifications
          </h2>
          <p className="text-gray-600 mb-4">
            Click states to toggle between insurance and non-insurance
            classifications.
          </p>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                {states.map((state) => (
                  <button
                    key={state.state_code}
                    onClick={() =>
                      handleStateToggle(state.state_code, state.classification)
                    }
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${
                      state.classification === "insurance"
                        ? "border-blue-500 bg-blue-50 text-blue-800"
                        : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-bold">{state.state_code}</div>
                    <div className="text-xs mt-1">
                      {state.classification === "insurance"
                        ? "Insurance"
                        : "Non-Ins."}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-50"></span>
                  Insurance States
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-50"></span>
                  Non-Insurance States
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* API KEYS TAB */}
      {activeTab === "api-keys" && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">API Keys</h2>
              <p className="text-gray-600 text-sm">
                Manage API keys for external portal access
              </p>
            </div>
            <button
              onClick={handleCreateApiKey}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Create API Key
            </button>
          </div>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No API keys created yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="border rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">{key.name}</div>
                    <div className="text-sm text-gray-500">
                      <code className="bg-gray-100 px-2 py-0.5 rounded">
                        {key.key_prefix}...
                      </code>
                      <span className="ml-3">
                        {key.can_create && "Create "}
                        {key.can_read && "Read "}
                        {key.can_update && "Update "}
                        {key.can_delete && "Delete"}
                      </span>
                    </div>
                    {key.last_used_at && (
                      <div className="text-xs text-gray-400 mt-1">
                        Last used: {formatDate(key.last_used_at)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${key.is_active && !key.revoked_at ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                    >
                      {key.is_active && !key.revoked_at ? "Active" : "Revoked"}
                    </span>
                    {key.is_active && !key.revoked_at && (
                      <button
                        onClick={() => handleRevokeApiKey(key.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerOffers;
